import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import type { GeneratePromptsForImageMutation, GeneratePromptsForImageMutationVariables } from '@vizit/api';
import { APIError, APIErrorType, getAPIErrorCode, queryKeys, useGeneratePromptsForImageMutation } from '@vizit/api';
import { useSearchCollectionImagesQuery, useSearchImagesQuery } from '@vizit/api';
import { AlertLevel, useToast } from '@vizit/atoms';
import { FEATURE_FLAGS, SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS, SPARK_IMAGE_TYPE_CONFIG } from '@vizit/constants';
import { useFeatureFlag, useShowSparkImagePrompts } from '@vizit/feature-flags';
import * as React from 'react';
import { useSparkData } from '../../components/asset-details-sparks-tab-panel/asset-details-sparks-tab-panel.hooks';
import { AssetDetailsTabOptions } from '../../components/asset-details-tabs/asset-details-tabs';
import { getSparkImagesQueryKey, useSparkImages } from '../../components/spark-images-grid';
import { hasServedUrl, isProcessedImage } from '../../components/spark-images-grid/spark-images-grid.utils';
import { rawComparisonScore } from '../../components/spark-images-grid/spark-images-grid.utils';
import { useCustomPairScore, useGetImageGenBatchStatus, useGetOriginalImage } from '../../hooks';
import type { AssetDetailsOrigin } from '../../utils';
import { SPARK_NOT_READY_MESSAGE, isGeneratedImageBySource, isSparkNotReadyError } from '../../utils';
import { AnalysisArtifacts, AssetDetailsContext } from './asset-details-context';
import { AssetDetailsActionType, assetDetailsReducer } from './asset-details-provider.state';

const DEFAULT_GENERATE_MORE_ERROR = 'Failed to generate images. Please try again.';

const GENERATE_MORE_ERROR_MESSAGES: Record<string, string> = {
  REMIX_IMAGE_NOT_SCORED: 'This image is still being scored. Try again once it has a score.',
  GENERATED_IMAGE_NOT_ALLOWED: 'This image cannot be used to generate more yet. Try again shortly.',
  GENERATE_MORE_NO_SPARK_OUTPUT: 'This image needs a completed round of Spark images before you can generate more.',
  GENERATE_MORE_NO_PRIOR_BATCH: 'This image needs a completed round of Spark images before you can generate more.',
  USER_PROMPT_TOO_LONG: 'That prompt is too long. Shorten it and try again.',
};

// Images whose AI children predate spark_outputs have children but no round the API accepts.
const NO_ROUND_ERROR_CODES = new Set(['GENERATE_MORE_NO_SPARK_OUTPUT', 'GENERATE_MORE_NO_PRIOR_BATCH']);

/** A prompted generate_more on a root that the API says has no round, which a first round can serve instead. */
const shouldStartFirstRoundInstead = (error: unknown, variables: GeneratePromptsForImageMutationVariables) =>
  !variables.isRemix &&
  Boolean(variables.hasPriorRound) &&
  Boolean(variables.prompt?.trim()) &&
  NO_ROUND_ERROR_CODES.has(getAPIErrorCode(error) ?? '');

/** The reducer action that shows each tab. */
const TAB_SELECTED_ACTIONS: Record<AssetDetailsTabOptions, AssetDetailsActionType> = {
  [AssetDetailsTabOptions.HeroScore]: AssetDetailsActionType.HeroScoreTabSelected,
  [AssetDetailsTabOptions.ImageAnalysis]: AssetDetailsActionType.ImageAnalysisTabSelected,
  [AssetDetailsTabOptions.SparkIdeas]: AssetDetailsActionType.SparkTabSelected,
  [AssetDetailsTabOptions.SparkImages]: AssetDetailsActionType.SparkImagesTabSelected,
};

/** How a tab change lands in the browser history. */
interface AssetDetailsTabChangeOptions {
  /**
   * Replaces the current history entry instead of adding one. The page's own switch to the tab it opens on replaces,
   * so Back leaves the page; a tab the user picks adds an entry.
   * @type {boolean}
   * @optional
   * @default false
   */
  replace?: boolean;
}

interface AssetDetailsProviderProps {
  /**
   * The asset ID.
   */
  assetId: string;
  /**
   * The app surface this page was opened from (drives the class-change dialog).
   */
  origin?: AssetDetailsOrigin;
  /**
   * The collection the asset was opened from, when any (drives the scored-against pair).
   */
  collectionId?: string;
  /**
   * The custom benchmark the collection showed with no category under it, as a picker key (`custom:<id>`), when the
   * asset was opened from there. Until the collection's own pair is under it, the asset is not scored against any.
   */
  awaitingCategoryBenchmarkKey?: string;
  /**
   * The portfolio ID.
   */
  workspaceId: string;
  /**
   * The organization ID.
   */
  organizationId: string;
  /**
   * The children of the component.
   */
  children: React.ReactNode;
  /**
   * The tab the URL names. Without one the page opens on Analysis, or on Hero score for a hero.
   * @type {AssetDetailsTabOptions}
   * @optional
   */
  selectedTab?: AssetDetailsTabOptions;
  /**
   * The function to call when the tab changes.
   */
  onTabChange?: (tab: AssetDetailsTabOptions, options?: AssetDetailsTabChangeOptions) => void;
  /**
   * The function to call when the asset details fail to load
   *
   * @param error - The error that occurred
   */
  onAuthenticationError?: (error: unknown | APIError) => void;
  /**
   * The function to call when the jump-to hover action is pressed on a
   * generated image in the Spark Images tab. Navigates to that image's own
   * details page. When not provided, the jump-to action is not shown.
   */
  onJumpToImage?: (imageId: string) => void;
  /**
   * The function to call when the back overlay action is pressed on the image
   * preview. Navigates to the parent (source) image's details page. When not
   * provided, the back action is not shown.
   */
  onBackToImage?: (imageId: string) => void;
}

export const AssetDetailsProvider = (props: AssetDetailsProviderProps) => {
  const {
    assetId,
    origin,
    collectionId,
    awaitingCategoryBenchmarkKey,
    children,
    workspaceId,
    organizationId,
    selectedTab,
    onTabChange: onTabChangeProp,
    onAuthenticationError,
    onJumpToImage,
    onBackToImage,
  } = props;

  const { data: originalImageData, isLoading: isOriginalImageLoading } = useGetOriginalImage({
    workspaceId,
    organizationId,
    assetId,
    onAuthenticationError,
  });

  const {
    pair: customPair,
    score: customPairScore,
    rawScore: customPairRawScore,
    collectionScore,
    collectionRawScore,
    isLoading: isCustomPairScoreLoading,
    isPairLoading: isCustomPairLoading,
    isCollectionError,
    isAwaitingCategory,
  } = useCustomPairScore({
    workspaceId,
    collectionId,
    awaitingCategoryBenchmarkKey,
    imageId: assetId,
  });

  // A pair in play, whichever kind, scores the original and the image's own score stands only without one. Until the
  // collection answers or fails, or while the pair has not scored the image, the original reads null rather than its
  // own score, as the details header does, so it never flashes a score the pair then replaces. Nor does it while the
  // collection's benchmark awaits a category, as the header asks for one rather than show a score.
  const ownScoreStands = !isCustomPairLoading && !isCollectionError && !isAwaitingCategory && !customPair;
  const originalScore = ownScoreStands ? (originalImageData?.display_score ?? null) : collectionScore;
  const originalRawScore = ownScoreStands ? rawComparisonScore(originalImageData) : collectionRawScore;

  const isHeroSparkImagesEnabled = useFeatureFlag(FEATURE_FLAGS.HERO_SPARK_IMAGES, false);
  const isSparkImagePromptsEnabled = useShowSparkImagePrompts();

  const queryClient = useQueryClient();
  const { addToast } = useToast();

  // Image generation batch management
  // Tracks the current batch ID for polling. Sources:
  // 1. From mutation response when user triggers generation
  // 2. From originalImageData.most_recent_batch_id on page load (to resume polling)
  const [currentBatchId, setCurrentBatchId] = React.useState<string | null>(null);

  // Resume polling if there's a batch in progress on page load
  // This ensures that if a user refreshes the page or navigates back while
  // generation is in progress, we continue polling for the batch status
  // biome-ignore lint/correctness/useExhaustiveDependencies: currentBatchId is intentionally excluded to prevent infinite loops
  React.useEffect(() => {
    const mostRecentBatchId = originalImageData?.most_recent_batch_id;
    if (mostRecentBatchId && !currentBatchId) {
      setCurrentBatchId(mostRecentBatchId);
    }
  }, [originalImageData?.most_recent_batch_id]);

  // Default to Hero Score tab when viewing a hero image and no specific tab
  // was chosen; a tab the URL names, Analysis included, is kept. Keyed by
  // assetId (not a boolean) so in-place navigation to another hero re-runs the
  // redirect regardless of effect ordering. The redirect replaces the history
  // entry, so one Back leaves the page.
  const heroRedirectedForAssetId = React.useRef<string | null>(null);
  const previousAssetId = React.useRef(assetId);
  // The tabs the page itself sent the route to, oldest first, until the route lands on them.
  const expectedRouteTabs = React.useRef<AssetDetailsTabOptions[]>([]);

  React.useEffect(() => {
    if (
      originalImageData?.image_type === 'hero' &&
      selectedTab === undefined &&
      heroRedirectedForAssetId.current !== assetId
    ) {
      heroRedirectedForAssetId.current = assetId;
      if (onTabChangeProp) {
        expectedRouteTabs.current.push(AssetDetailsTabOptions.HeroScore);
        onTabChangeProp(AssetDetailsTabOptions.HeroScore, { replace: true });
      }
      dispatch({
        type: AssetDetailsActionType.HeroScoreTabSelected,
        payload: { currentTab: AssetDetailsTabOptions.HeroScore, originalAssetId: assetId },
      });
    }
  }, [originalImageData?.image_type, selectedTab, assetId, onTabChangeProp]);

  // The query `useSparkImages` reads, so cache writes/invalidations target the displayed grid.
  const sparkImagesQueryKey = React.useMemo(
    () => [
      ...getSparkImagesQueryKey({
        workspaceId,
        imageId: assetId,
        imageType: originalImageData?.image_type,
        customPair,
      }),
    ],
    [workspaceId, assetId, originalImageData?.image_type, customPair],
  );

  // The grid sorts unscored rows first, so a failed round can fill its first page
  // and hide every processed image. Ascending puts them last, so one row answers
  // whether any processed image exists. Only a prompted press reads it.
  // Under a pair the children are scored under the pair, so it is the collection listing that answers.
  const canReadLowestScoredChild =
    !!workspaceId && !!assetId && !!originalImageData && isSparkImagePromptsEnabled && !isCustomPairLoading;
  const { refetch: refetchLowestScoredOwnChild, data: lowestScoredOwnChild } = useSearchImagesQuery(
    {
      workspace_id: workspaceId,
      parent_id: [assetId],
      source: ['generated'],
      sort_by: SPARK_IMAGE_TYPE_CONFIG[originalImageData?.image_type ?? 'carousel'].sortBy,
      sort_direction: 'asc',
      limit: 1,
    },
    { enabled: canReadLowestScoredChild && !customPair },
  );
  const { refetch: refetchLowestScoredPairChild, data: lowestScoredPairChild } = useSearchCollectionImagesQuery(
    {
      workspace_id: workspaceId,
      collection_id: customPair?.collectionId ?? '',
      parent_id: [assetId],
      source: ['generated'],
      sort_by: 'pair_score',
      sort_direction: 'asc',
      limit: 1,
      product_category_id: customPair?.productCategoryId,
      benchmark_id: customPair?.benchmarkId ?? undefined,
      retailer: customPair?.retailer ?? undefined,
    },
    { enabled: canReadLowestScoredChild && !!customPair },
  );
  const refetchLowestScoredChild = customPair ? refetchLowestScoredPairChild : refetchLowestScoredOwnChild;
  // A round exists once a child is rendered; under a pair its score may still be on its way.
  const hasProcessedChild = customPair
    ? (lowestScoredPairChild?.data.some(hasServedUrl) ?? false)
    : (lowestScoredOwnChild?.data.some(isProcessedImage) ?? false);

  // The asset the API last turned away from Spark images; the tab doesn't ask again for it.
  const [sparkNotReadyAssetId, setSparkNotReadyAssetId] = React.useState<string | null>(null);

  const handleBatchComplete = React.useCallback(() => {
    setCurrentBatchId(null);
    refetchLowestScoredChild();
  }, [refetchLowestScoredChild]);

  // - Polls every 2 seconds when status is 'PROCESSING'
  // - Continues polling in background even when tab is not active
  // - Shows error toasts on failure
  // - Invalidates spark images query on completion
  const { isImageGenerationInProgress, hasGenerationError, generationErrorMessage, clearError } =
    useGetImageGenBatchStatus({
      assetId,
      batchId: currentBatchId,
      sparkImagesQueryKey,
      onBatchComplete: handleBatchComplete,
    });

  const originalImageQueryKey = React.useMemo(
    () =>
      queryKeys.images.detail({ organization_id: organizationId, workspace_id: workspaceId, image_id: assetId })
        .queryKey,
    [organizationId, workspaceId, assetId],
  );

  const handleGenerationSuccess = React.useCallback(
    (data: GeneratePromptsForImageMutation) => {
      // The endpoint also answers with a validate-only shape that resolves the generation mode
      // without starting a batch; only the generating shape carries a batch to track.
      const batchId = 'batch_id' in data ? data.batch_id : undefined;

      if (!batchId) {
        Sentry.addBreadcrumb({
          message: 'Image generation validated; no batch started.',
          level: 'info',
          data: {
            generation_mode: 'generation_mode' in data ? data.generation_mode : undefined,
            asset_id: assetId,
            organization_id: organizationId,
            workspace_id: workspaceId,
          },
        });

        return;
      }

      Sentry.addBreadcrumb({
        message: 'Image generation initiated successfully.',
        level: 'info',
        data: {
          batch_id: batchId,
          asset_id: assetId,
          organization_id: organizationId,
          workspace_id: workspaceId,
        },
      });

      setCurrentBatchId(batchId);
      // Invalidate original image query to fetch updated most_recent_batch_id
      queryClient.invalidateQueries({ queryKey: originalImageQueryKey });
    },
    [queryClient, originalImageQueryKey, assetId, organizationId, workspaceId],
  );

  const generatePromptsForImageMutation = useGeneratePromptsForImageMutation({
    onSuccess: handleGenerationSuccess,
    onError: (error, variables) => {
      if (shouldStartFirstRoundInstead(error, variables)) {
        Sentry.addBreadcrumb({
          message: 'No round to generate more from, starting a first round',
          level: 'info',
          data: { asset_id: assetId, error_code: getAPIErrorCode(error) },
        });
        return;
      }
      if (isSparkNotReadyError(error)) {
        Sentry.addBreadcrumb({
          message: 'Spark images not available for the image yet',
          level: 'info',
          data: { asset_id: assetId, error_code: getAPIErrorCode(error) },
        });
        return;
      }
      Sentry.captureException(error, {
        level: 'error',
        tags: {
          feature: SENTRY_FEATURE_TAGS.Asset,
          stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Asset].assetImageGeneration,
        },
        extra: { asset_id: assetId, organization_id: organizationId, workspace_id: workspaceId },
      });
    },
  });

  const generateImagesForAsset = React.useCallback(async () => {
    Sentry.addBreadcrumb({
      message: 'Initiating image generation for asset',
      level: 'info',
      data: { asset_id: assetId, organization_id: organizationId, workspace_id: workspaceId },
    });

    try {
      await generatePromptsForImageMutation.mutateAsync({ image_id: assetId });
    } catch (error) {
      // The mutation reports any other failure; this path has no press to explain a toast for it.
      if (isSparkNotReadyError(error)) {
        setSparkNotReadyAssetId(assetId);
        addToast({ title: 'Image generation failed', description: SPARK_NOT_READY_MESSAGE, level: AlertLevel.Error });
      }
    }
  }, [generatePromptsForImageMutation, assetId, organizationId, workspaceId, addToast]);

  const [assetDetailsState, dispatch] = React.useReducer(assetDetailsReducer, {
    currentTab: selectedTab ?? AssetDetailsTabOptions.ImageAnalysis,
    selectedArtifact: AnalysisArtifacts.OriginalImage,
    selectedGeneratedImageId: assetId, // Default to original asset ID
    hideImagesNotHigherThanOriginal: true, // Hide non-improving images by default
  });

  // Reset viewer state and batch tracking when the asset changes. The router
  // reuses this mounted provider when only the assetId param changes
  // (jump-to, breadcrumb thumbnails), so without this the reducer would keep
  // showing the previous asset's selection in the artifact viewer, and the
  // previous asset's generation batch would keep polling (and block
  // generation) under the new page. The hero-redirect guard needs no reset
  // here: it is keyed by assetId. Declared after the reducer and the
  // batch-status hook so `dispatch` and `clearError` exist when the deps are
  // evaluated.
  React.useEffect(() => {
    if (previousAssetId.current !== assetId) {
      previousAssetId.current = assetId;
      // If the new asset's data is already cached, resume its in-flight batch
      // directly — the resume effect above won't re-fire in that case because
      // `most_recent_batch_id` already changed in this same commit. Otherwise
      // clear, and the resume effect picks the batch up when data arrives.
      setCurrentBatchId(originalImageData?.id === assetId ? (originalImageData.most_recent_batch_id ?? null) : null);
      // Any persisted generation error belongs to the previous asset's batch, and so does a not-ready answer: a later
      // visit to that asset may find it ready.
      clearError();
      setSparkNotReadyAssetId(null);
      // Re-sync the tab from the route. For a cached hero arriving without a
      // tab, mirror the hero-redirect decision here instead of the
      // stale URL value — the redirect effect may have already run in this
      // same commit and must not be overridden by declaration order.
      const isCachedHero = originalImageData?.id === assetId && originalImageData.image_type === 'hero';
      const nextTab =
        selectedTab ?? (isCachedHero ? AssetDetailsTabOptions.HeroScore : AssetDetailsTabOptions.ImageAnalysis);
      dispatch({
        type: AssetDetailsActionType.OriginalAssetChanged,
        payload: { originalAssetId: assetId, currentTab: nextTab },
      });
    }
  }, [assetId, originalImageData, clearError, selectedTab]);

  // Spark data lives under the provider so consumers can access via context
  const { sparkState, sparkContent, generateIdea, shouldRunGeneration } = useSparkData({
    workspaceId,
    assetId,
    currentTab: assetDetailsState.currentTab,
    imageData: originalImageData || null,
    isImageLoading: isOriginalImageLoading || isCustomPairScoreLoading,
    customPairScore,
  });

  const {
    images: sparkImages,
    isLoading: isSparkImagesLoading,
    hasError: hasSparkImagesError,
    hasNextPage: hasSparkImagesNextPage,
    isFetchingNextPage: isSparkImagesFetchingNextPage,
    fetchNextPage: fetchSparkImagesNextPage,
    totalCount: sparkImagesTotalCount,
  } = useSparkImages({
    workspaceId,
    imageId: assetId,
    enabled: !!workspaceId && !!assetId,
    isImageGenerationInProgress,
    imageType: originalImageData?.image_type,
    customPair,
    isCustomPairLoading,
  });

  const handleOnArtifactChange = (option: AnalysisArtifacts) => {
    dispatch({
      type: AssetDetailsActionType.SelectedArtifactChanged,
      payload: { selectedArtifact: option },
    });
  };

  const handleOnSelectedGeneratedImageChange = React.useCallback((imageId: string) => {
    dispatch({
      type: AssetDetailsActionType.SelectedGeneratedImageChanged,
      payload: { selectedGeneratedImageId: imageId },
    });
  }, []);

  const handleOnHideImagesNotHigherThanOriginalChange = React.useCallback((hide: boolean) => {
    dispatch({
      type: AssetDetailsActionType.HideImagesNotHigherThanOriginalChanged,
      payload: { hideImagesNotHigherThanOriginal: hide },
    });
  }, []);

  // Only the press path calls this: the generation that fires on tab switch has
  // no press to explain a toast.
  const handleGenerateMoreError = React.useCallback(
    (error: unknown) => {
      addToast({
        title: 'Image generation failed',
        description: isSparkNotReadyError(error)
          ? SPARK_NOT_READY_MESSAGE
          : (GENERATE_MORE_ERROR_MESSAGES[getAPIErrorCode(error) ?? ''] ?? DEFAULT_GENERATE_MORE_ERROR),
        level: AlertLevel.Error,
      });

      // Only an expired session (401 or expired JWT) re-auths; validation,
      // server, and network failures stop at the toast.
      if (error instanceof APIError && error.type === APIErrorType.NotAuthenticated) {
        onAuthenticationError?.(error);
      }
    },
    [addToast, onAuthenticationError],
  );

  const { mutateAsync: generatePrompts } = generatePromptsForImageMutation;
  const mutateWithFirstRoundFallback = React.useCallback(
    async (variables: GeneratePromptsForImageMutationVariables) => {
      try {
        return await generatePrompts(variables);
      } catch (error) {
        if (!shouldStartFirstRoundInstead(error, variables)) throw error;
        return generatePrompts({ ...variables, hasPriorRound: false });
      }
    },
    [generatePrompts],
  );

  const handleGenerateMoreImages = React.useCallback(
    async (prompt?: string) => {
      clearError();
      const variables: GeneratePromptsForImageMutationVariables = {
        image_id: assetId,
        prompt,
        // A generated image only accepts generate_more, prompt or no prompt.
        // Flag off keeps today's request: no body.
        isRemix: isSparkImagePromptsEnabled && isGeneratedImageBySource(originalImageData),
        // A round counts once it has a processed image; a failed round leaves
        // rows without output, and generate_more rejects those.
        hasPriorRound:
          hasProcessedChild || sparkImages.some((image) => (customPair ? hasServedUrl(image) : !image.isLoading)),
      };
      try {
        await mutateWithFirstRoundFallback(variables);
      } catch (error) {
        handleGenerateMoreError(error);
        // Rethrown so the footer keeps the text the user would have to retype.
        throw error;
      }
    },
    [
      mutateWithFirstRoundFallback,
      assetId,
      clearError,
      handleGenerateMoreError,
      originalImageData,
      isSparkImagePromptsEnabled,
      sparkImages,
      hasProcessedChild,
      customPair,
    ],
  );

  // Shows a tab with what showing it starts: an idea on Spark Ideas, a first round on Spark Images. A click and the
  // route (Back, Forward) both show tabs through it, so they start the same things.
  const showTab = (option: AssetDetailsTabOptions): Promise<void> | undefined => {
    dispatch({ type: TAB_SELECTED_ACTIONS[option], payload: { currentTab: option, originalAssetId: assetId } });
    if (option === AssetDetailsTabOptions.SparkIdeas) {
      // Only run generation if there is no completed idea ready to show
      if (shouldRunGeneration) {
        Sentry.addBreadcrumb({
          message: 'Auto-generating idea on tab switch.',
          level: 'info',
          data: { asset_id: assetId, organization_id: organizationId, workspace_id: workspaceId },
        });
        return generateIdea();
      }
    } else if (option === AssetDetailsTabOptions.SparkImages) {
      // With prompts on, opening the tab never generates: a round costs real
      // money and lands on the asset permanently, so it waits for a press.
      // Flag off keeps the auto-generate. It needs originalImageData resolved,
      // since an unresolved image_type reads as non-hero, and skips generated
      // images, which only generate from the button.
      const isImageGenEnabled =
        !isSparkImagePromptsEnabled &&
        !!originalImageData &&
        !isGeneratedImageBySource(originalImageData) &&
        (originalImageData.image_type !== 'hero' || isHeroSparkImagesEnabled);

      // An empty grid only means no images once the grid has read them, under the pair the collection names. A
      // failed read says nothing, and a round costs money.
      const hasReadNoImages =
        sparkImages.length === 0 && !isSparkImagesLoading && !isCustomPairLoading && !hasSparkImagesError;

      if (hasReadNoImages && !isImageGenerationInProgress && isImageGenEnabled && sparkNotReadyAssetId !== assetId) {
        Sentry.addBreadcrumb({
          message: 'Auto-generating images on tab switch.',
          level: 'info',
          data: { asset_id: assetId, organization_id: organizationId, workspace_id: workspaceId },
        });

        generateImagesForAsset();
      }
    }
    return undefined;
  };

  // The route hears of a picked tab before its idea is generated, so the navigation cannot land after a later pick.
  const handleOnTabChange = async (option: AssetDetailsTabOptions) => {
    if (assetDetailsState.currentTab === option) {
      return;
    }
    const shown = showTab(option);
    if (onTabChangeProp) {
      expectedRouteTabs.current.push(option);
      await onTabChangeProp(option);
    }
    await shown;
  };

  // Follow the route's tab within the same asset, as on Back and Forward. The route also lands on the tabs the page
  // sent it to (a click, the hero default), possibly after a later pick; those it has already shown, so they are
  // dropped instead of followed. An asset change re-syncs the tab above.
  const showTabRef = React.useRef(showTab);
  showTabRef.current = showTab;
  const previousRoute = React.useRef({ assetId, selectedTab });
  React.useEffect(() => {
    const previous = previousRoute.current;
    previousRoute.current = { assetId, selectedTab };
    if (previous.assetId !== assetId) {
      expectedRouteTabs.current = [];
      return;
    }
    if (previous.selectedTab === selectedTab) {
      return;
    }
    const expectedIndex = selectedTab ? expectedRouteTabs.current.indexOf(selectedTab) : -1;
    if (expectedIndex !== -1) {
      expectedRouteTabs.current.splice(0, expectedIndex + 1);
      return;
    }
    expectedRouteTabs.current = [];
    const isHero = originalImageData?.image_type === 'hero';
    const tab = selectedTab ?? (isHero ? AssetDetailsTabOptions.HeroScore : AssetDetailsTabOptions.ImageAnalysis);
    if (tab !== assetDetailsState.currentTab) {
      void showTabRef.current(tab);
    }
  }, [assetId, selectedTab, originalImageData?.image_type, assetDetailsState.currentTab]);

  return (
    <AssetDetailsContext
      value={{
        ...assetDetailsState,
        assetId,
        origin,
        collectionId,
        awaitingCategoryBenchmarkKey,
        workspaceId,
        organizationId,
        onArtifactChange: handleOnArtifactChange,
        onTabChange: handleOnTabChange,
        onAuthenticationError,
        sparkState,
        sparkContent,
        generateIdea,
        onSelectedGeneratedImageChange: handleOnSelectedGeneratedImageChange,
        onJumpToImage,
        onBackToImage,
        onHideImagesNotHigherThanOriginalChange: handleOnHideImagesNotHigherThanOriginalChange,
        originalImageData: originalImageData || null,
        isOriginalImageLoading,
        customPair,
        customPairScore,
        customPairRawScore,
        originalScore,
        originalRawScore,
        generateMoreImages: handleGenerateMoreImages,
        sparkImages,
        isSparkImagesLoading,
        hasSparkImagesError,
        hasSparkImagesNextPage,
        isSparkImagesFetchingNextPage,
        fetchSparkImagesNextPage,
        sparkImagesTotalCount,
        // Covers both the initial mutation HTTP (`isPending`) and the
        // subsequent batch-status polling (`isImageGenerationInProgress`) so
        // consumers see a single "in-flight" signal across all API calls the
        // generation flow fans out to.
        isGenerating: isImageGenerationInProgress || generatePromptsForImageMutation.isPending,
        hasBatchGenerationError: hasGenerationError,
        batchGenerationErrorMessage: generationErrorMessage,
      }}
    >
      {children}
    </AssetDetailsContext>
  );
};

export type { AssetDetailsTabChangeOptions };
